import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const PickupCalendar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const productId = searchParams.get('product_id') || '';
  const quantity = searchParams.get('quantity') || '1';
  const cartIds = searchParams.get('cart_ids') || '';

  const yearParam = searchParams.get('year');
  const monthParam = searchParams.get('month');
  const dateParam = searchParams.get('date') || '';
  const pickupTimeParam = searchParams.get('pickup_time') || '';

  const selectedCouponId = searchParams.get('selected_coupon_id') || '';

  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dateParam);
  const [selectedTime, setSelectedTime] = useState(pickupTimeParam);

  useEffect(() => {
    const params = new URLSearchParams();

    if (productId) params.set('product_id', productId);
    if (quantity) params.set('quantity', quantity);
    if (cartIds) params.set('cart_ids', cartIds);
    if (yearParam) params.set('year', yearParam);
    if (monthParam) params.set('month', monthParam);
    if (dateParam) params.set('date', dateParam);
    if (pickupTimeParam) params.set('pickup_time', pickupTimeParam);
    if (selectedCouponId) params.set('selected_coupon_id', selectedCouponId);
    

    axios.get(`/api/pickup/calendar?${params.toString()}`)
      .then((res) => {
        const d = res.data;
        setData(d);
        setSelectedDate(d.selected_date || '');
        setSelectedTime(d.selected_time || '');
      })
      .catch((err) => {
        alert(err.response?.data?.message || '픽업 달력을 불러올 수 없습니다.');
        navigate(-1);
      });
  }, [productId, quantity, cartIds, yearParam, monthParam, dateParam, pickupTimeParam, selectedCouponId, navigate]);

  const goMonth = (year, month) => {
    const params = new URLSearchParams();

    if (productId) params.set('product_id', productId);
    if (quantity) params.set('quantity', quantity);
    if (cartIds) params.set('cart_ids', cartIds);

    params.set('year', String(year));
    params.set('month', String(month));

    if (selectedDate) params.set('date', selectedDate);
    if (selectedTime) params.set('pickup_time', selectedTime);

    if (selectedCouponId) params.set('selected_coupon_id', selectedCouponId);
    
    navigate(`/pickup/calendar?${params.toString()}`, {
      state: location.state
    });
  };

  const goDate = (dateStr) => {
    const params = new URLSearchParams();

    if (productId) params.set('product_id', productId);
    if (quantity) params.set('quantity', quantity);
    if (cartIds) params.set('cart_ids', cartIds);

    params.set('year', String(data.now_year));
    params.set('month', String(data.now_month));
    params.set('date', dateStr);

    if (selectedTime) params.set('pickup_time', selectedTime);

    if (selectedCouponId) params.set('selected_coupon_id', selectedCouponId);

    navigate(`/pickup/calendar?${params.toString()}`, {
      state: location.state
    });
  };

  const handleSubmit = () => {
    if (!selectedDate || !selectedTime) {
      alert('날짜와 시간을 선택해 주세요.');
      return;
    }

    const pickupDatetime = `${selectedDate} ${selectedTime}:00`;

    const params = new URLSearchParams();

    if (data.is_cart_mode) {
      params.set('cart_ids', data.cart_ids || '');
      params.set('reserve_yn', 'Y');
      params.set('pickup_date', selectedDate);
      params.set('pickup_time', selectedTime);
      params.set('pickup_datetime', pickupDatetime);

      if (selectedCouponId) {
        params.set('selected_coupon_id', selectedCouponId);
      }

      navigate(`/order/selected?${params.toString()}`, {
        state: {
          cartIds: (data.cart_ids || '')
            .split(',')
            .map(v => Number(v))
            .filter(Boolean),
          selectedCouponId
        }
      });
    } else {
      params.set('product_id', String(data.product_id));
      params.set('quantity', String(data.quantity));
      params.set('reserve_yn', 'Y');
      params.set('pickup_date', selectedDate);
      params.set('pickup_time', selectedTime);
      params.set('pickup_datetime', pickupDatetime);

      navigate(`/order/now?${params.toString()}`, {
        state: {
          ...location.state,
          product: {
            ...location.state?.product,
            id: Number(data.product_id),
            quantity: Number(data.quantity)
          },
          selectedCouponId
        }
      });
    }
  };

  const handleCancel = () => {
    if (!data) return;

    if (data.is_cart_mode) {
      const params = new URLSearchParams();

      params.set('cart_ids', data.cart_ids || '');

      if (selectedCouponId) {
          params.set('selected_coupon_id', selectedCouponId);
      }

      navigate(`/order/selected?cart_ids=${encodeURIComponent(data.cart_ids || '')}`, {
        state: {
          cartIds: (data.cart_ids || '')
            .split(',')
            .map(v => Number(v))
            .filter(Boolean),
          selectedCouponId
        }
      });
    } else {
      navigate(
        `/order/now?product_id=${encodeURIComponent(data.product_id)}&quantity=${encodeURIComponent(data.quantity)}`,
        {
          state: {
            ...location.state,
            product: {
              ...location.state?.product,
              id: Number(data.product_id),
              quantity: Number(data.quantity)
            },
            selectedCouponId
          }
        }
      );
    }
  };

  if (!data) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>로딩 중...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.calendarHead}>
            <div style={styles.monthTitle}>{data.now_year}년 {data.now_month}월</div>

            <div style={styles.monthNav}>
              {data.can_prev ? (
                <button
                  type="button"
                  style={styles.navBtn}
                  onClick={() => goMonth(data.prev_year, data.prev_month)}
                >
                  <span style={{ position: 'relative', top: '-2px' }}>‹</span>
                </button>
              ) : (
                <span style={styles.navDisabled}>
                  <span style={{ position: 'relative', top: '-2px' }}>‹</span>
                </span>
              )}

              {data.can_next ? (
                <button
                  type="button"
                  style={styles.navBtn}
                  onClick={() => goMonth(data.next_year, data.next_month)}
                >
                  <span style={{ position: 'relative', top: '-2px' }}>›</span>
                </button>
              ) : (
                <span style={styles.navDisabled}>
                  <span style={{ position: 'relative', top: '-2px' }}>›</span>
                </span>
              )}
            </div>
          </div>

          <table style={styles.table}>
            <colgroup>
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
              <col style={{ width: '14.285%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.sun }}>일</th>
                <th style={styles.th}>월</th>
                <th style={styles.th}>화</th>
                <th style={styles.th}>수</th>
                <th style={styles.th}>목</th>
                <th style={styles.th}>금</th>
                <th style={{ ...styles.th, ...styles.sat }}>토</th>
              </tr>
            </thead>
            <tbody>
              {data.calendar_rows.map((week, weekIdx) => (
                <tr key={weekIdx}>
                  {week.map((day, dayIdx) => {
                    if (!day.in_current_month) {
                      return <td key={dayIdx} style={styles.td}></td>;
                    }

                    if (day.is_disabled) {
                      return (
                        <td key={dayIdx} style={styles.td}>
                          <span
                            style={{
                              ...styles.day,
                              ...(day.is_sunday ? styles.sun : {}),
                              ...(day.is_saturday ? styles.sat : {}),
                              ...styles.dayDisabled
                            }}
                          >
                            {day.day}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={dayIdx} style={styles.td}>
                        <button
                          type="button"
                          onClick={() => goDate(day.date_str)}
                          style={{
                            ...styles.dayBtn,
                            ...(day.is_selected ? styles.daySelected : {}),
                            ...(day.is_sunday ? styles.sun : {}),
                            ...(day.is_saturday ? styles.sat : {})
                          }}
                        >
                          {day.day}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>픽업 시간 선택</div>

          {data.is_selected_date_valid ? (
            <>
              <div style={styles.selectedInfo}>
                선택한 날짜: {data.selected_date}
                {selectedTime ? ` / 시간: ${selectedTime}` : ''}
              </div>

              <div style={styles.timeGrid}>
                {data.time_slots.map((time) => (
                  <label key={time} style={styles.timeItem}>
                    <input
                      type="radio"
                      name="pickup_time"
                      value={time}
                      checked={selectedTime === time}
                      onChange={() => setSelectedTime(time)}
                      style={styles.timeInput}
                    />
                    <span style={selectedTime === time ? styles.timeLabelActive : styles.timeLabel}>
                      {time}
                    </span>
                  </label>
                ))}
              </div>

              <div style={styles.btnRow}>
                <button type="button" style={styles.btnBasic} onClick={handleCancel}>
                  취소
                </button>
                <button type="button" style={styles.btnPrimary} onClick={handleSubmit}>
                  선택 완료
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={styles.selectedInfo}>
                먼저 예약 가능한 날짜를 선택해 주세요.
              </div>

              <div style={styles.btnRow}>
                <button type="button" style={styles.btnBasic} onClick={handleCancel}>
                  돌아가기
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    fontFamily: 'Arial, sans-serif',
    background: '#fff',
    margin: 0,
    padding: 0,
    minHeight: '100vh'
  },
  container: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    padding: '20px 5px 20px',
    boxSizing: 'border-box'
  },
  card: {
    background: '#fff',
    borderRadius: '2px',
    padding: '12px',
    marginBottom: '18px'
  },
  calendarHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px'
  },
  monthTitle: {
    fontSize: '22px',
    fontWeight: 700
  },
  monthNav: {
    display: 'inline-flex',
    gap: '8px'
  },
  navBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    lineHeight: '38px',
    borderRadius: '50%',
    textDecoration: 'none',
    color: '#111',
    background: '#fff',
    fontSize: '28px',
    fontWeight: 700,
    border: '1px solid #eee',
    cursor: 'pointer'
  },
  navDisabled: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    lineHeight: '38px',
    borderRadius: '50%',
    color: '#bbb',
    background: '#fff',
    fontSize: '28px',
    fontWeight: 700,
    border: '1px solid #eee',
    cursor: 'not-allowed'
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed'
  },
  th: {
    fontSize: '14px',
    padding: '15px 10px 15px',
    textAlign: 'center',
    fontWeight: 700,
    background: '#f5f5f7',
    border: 'none'
  },
  td: {
    height: '45px',
    textAlign: 'center',
    verticalAlign: 'middle',
    background: 'transparent',
    border: 'none'
  },
  dayBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    margin: '0 auto',
    borderRadius: '50%',
    textDecoration: 'none',
    color: '#111',
    fontWeight: 600,
    transition: 'all .15s ease',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer'
  },
  day: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    margin: '0 auto',
    borderRadius: '50%',
    textDecoration: 'none',
    color: '#111',
    fontWeight: 600
  },
  daySelected: {
    background: '#0d6efd',
    color: '#fff'
  },
  dayDisabled: {
    color: '#c7c7c7',
    pointerEvents: 'none',
    cursor: 'default'
  },
  sun: {
    color: '#000'
  },
  sat: {
    color: '#000'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '10px'
  },
  selectedInfo: {
    padding: '14px',
    borderRadius: '5px',
    background: '#f3f6ff',
    color: '#2f5fd7',
    fontWeight: 700,
    marginTop: '14px'
  },
  timeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '10px',
    marginTop: '14px'
  },
  timeItem: {
    position: 'relative',
    cursor: 'pointer'
  },
  timeInput: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none'
  },
  timeLabel: {
    display: 'block',
    textAlign: 'center',
    padding: '12px 8px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    background: '#fff',
    cursor: 'pointer',
    fontWeight: 600
  },
  timeLabelActive: {
    display: 'block',
    textAlign: 'center',
    padding: '12px 8px',
    border: '1px solid #0d6efd',
    borderRadius: '5px',
    background: '#0d6efd',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600
  },
  btnRow: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '20px'
  },
  btnBasic: {
    padding: '12px 18px',
    borderRadius: '5px',
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    textDecoration: 'none',
    fontWeight: 600,
    cursor: 'pointer'
  },
  btnPrimary: {
    padding: '12px 18px',
    borderRadius: '5px',
    border: '1px solid #0d6efd',
    background: '#0d6efd',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer'
  }
};

export default PickupCalendar;